/** Create/edit form for a Context. Validated against `createContextSchema`. */
import { useForm } from "@refinedev/react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  CONTEXT_TYPES,
  CONTEXT_MATURITY_STAGES,
  CONFIDENTIALITY,
  createContextSchema,
  type CreateContextInput,
} from "@aos/shared";

import { FormShell } from "../skills/_components/FormShell";
import {
  NumberField,
  optionsFromEnum,
  SelectField,
  TextAreaField,
  TextField,
} from "../skills/_components/form";

export interface ContextFormProps {
  action: "create" | "edit";
}

export function ContextForm({ action }: ContextFormProps) {
  const form = useForm<CreateContextInput>({
    resolver: zodResolver(createContextSchema),
    refineCoreProps: { resource: "contexts", action },
  });

  const {
    refineCore: { onFinish, formLoading },
    handleSubmit,
  } = form;

  return (
    <FormShell
      title={action === "create" ? "New Context" : "Edit Context"}
      resource="contexts"
      isSubmitting={formLoading}
      onSubmit={handleSubmit((values) => onFinish(values))}
    >
      <TextField
        form={form}
        name="name"
        label="Name"
        placeholder="Simple Task Processing"
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <SelectField
          form={form}
          name="context_type"
          label="Context Type"
          options={optionsFromEnum(CONTEXT_TYPES)}
        />
        <SelectField
          form={form}
          name="maturity_stage"
          label="Maturity Stage"
          options={optionsFromEnum(CONTEXT_MATURITY_STAGES)}
          allowEmpty
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <NumberField
          form={form}
          name="priority"
          label="Priority"
          placeholder="50"
          description="Higher = more weight during context resolution."
        />
        <TextField
          form={form}
          name="status"
          label="Status"
          placeholder="active"
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField
          form={form}
          name="host_type"
          label="Host Type"
          placeholder="company"
        />
        <TextField
          form={form}
          name="host_id"
          label="Host ID"
          placeholder="company_demo"
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <SelectField
          form={form}
          name="confidentiality"
          label="Confidentiality"
          options={optionsFromEnum(CONFIDENTIALITY)}
          allowEmpty
        />
        <TextField
          form={form}
          name="owner_user_id"
          label="Owner User ID"
          placeholder="user_…"
        />
      </div>
      <TextField
        form={form}
        name="boundary"
        label="Boundary"
        placeholder="What is inside / outside this context."
      />
      <TextAreaField
        form={form}
        name="description"
        label="Description"
        placeholder="What this context represents…"
      />
    </FormShell>
  );
}

export default ContextForm;
